import { useEffect } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import {
  HR_SUMMARY_TAB_ID,
  DEFAULT_CONTROL_MODAL_VALUE,
  selectControlModal,
  useReportStore,
} from '@/entities/report';

/**
 * Drives the add-ECG-bookmark button of the HR Summary tab. Opens the add-bookmark modal when it is not already showing, and registers a window keydown listener firing the same action on the F key, ignoring auto-repeat, Ctrl or Meta combinations and presses made while the tab zone is not hovered.
 *
 * @returns An object carrying onClickAddBookmark.
 */
const useButtonAddEcgBookmark = () => {
  const keyRecoil = KEY_RECOIL.HR_SUMMARY;

  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Writes the modal control state of the HR Summary tab. Forwards the value or updater to the store action along with this tab's key.
   *
   * @param v - Next control modal value or an updater producing it.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  /**
   * Reads the modal control flags of the HR Summary tab from the report store. Applies the control-modal selector for this tab key to a one-shot snapshot.
   *
   * @returns The current control modal state.
   */
  const getControlModal = () => selectControlModal(keyRecoil)(useReportStore.getState());

  /**
   * Opens the add-ECG-bookmark modal for the HR Summary tab. Resets the control modal to its default values with isShowAddEcgBookmark set, and does nothing when that modal is already open.
   *
   * @returns Nothing.
   */
  const onClickAddBookmark = () => {
    if (!getControlModal().isShowAddEcgBookmark) {
      setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowAddEcgBookmark: true });
    }
  };

  useEffect(() => {
    /**
     * Turns an F key press into an add-ECG-bookmark action for the HR Summary tab. Ignores auto-repeat, presses made while the tab zone exists but is not hovered, and any press held with Ctrl or Meta.
     *
     * @param event - Keyboard event delivered by the window keydown listener.
     * @returns Nothing.
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event?.repeat) {
        return;
      }
      const tabZone = document.getElementById(HR_SUMMARY_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      const { keyCode, ctrlKey, metaKey } = event;
      if (ctrlKey || metaKey) {
        return;
      }
      if (keyCode === 70) {
        // F
        onClickAddBookmark();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return {
    onClickAddBookmark,
  };
};

export default useButtonAddEcgBookmark;
